import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Input, Select, DatePicker, message, Upload, Progress, Spin, Form } from 'antd';
import { UploadOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import * as XLSX from 'xlsx';
import FieldMappingModal from './FieldMappingModal';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';
import { formatIndianCurrency } from '../../../utils/numberFormat';

const { Option } = Select;

const { RangePicker } = DatePicker;

const MigrateRechargeHistoryPage = () => {
  const [rechargeHistory, setRechargeHistory] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentJob, setCurrentJob] = useState(null);
  
  // Filters
  const [searchUser, setSearchUser] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [dateRange, setDateRange] = useState(null);
  
  const [form] = Form.useForm();

  const fetchRechargeHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchUser) params.append('user', searchUser);
      if (searchStatus) params.append('status', searchStatus);
      if (dateRange && dateRange.length === 2) {
        params.append('startDate', dateRange[0].format('YYYY-MM-DD'));
        params.append('endDate', dateRange[1].format('YYYY-MM-DD'));
      }
      
      const response = await authFetch(getApiEndpoint('/migration/recharge/history?' + params));
      if (response.ok) {
        const data = await response.json();
        setRechargeHistory(data.data || []);
      }
    } catch (error) {
      message.error('Failed to fetch recharge history');
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/recharge/jobs'));
      if (response.ok) {
        const data = await response.json();
        setJobs(data.data || []);
        if (data.data && data.data.length > 0) {
          setCurrentJob(data.data[0]);
        }
      }
    } catch (error) {
      message.error('Failed to fetch jobs');
    }
  };

  useEffect(() => {
    fetchRechargeHistory();
    fetchJobs();
  }, []);

  const handleUpload = async (file) => {
    console.log('handleUpload called with file:', file.name);
    
    // Read Excel file to get headers
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        console.log('FileReader onload triggered');
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const headers = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })[0] || [];
        
        console.log('Excel headers:', headers);
        
        setExcelHeaders(headers);
        setPendingFile(file);
        setShowMappingModal(true);
        console.log('showMappingModal set to true');
      } catch (error) {
        console.error('Error reading Excel:', error);
        message.error('Failed to read Excel file: ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleMappingConfirm = async (mappings) => {
    if (!pendingFile) return;
    
    setUploadLoading(true);
    setShowMappingModal(false);
    
    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('mappings', JSON.stringify(mappings));

    try {
      const response = await authFetch(getApiEndpoint('/migration/recharge/upload'), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const job = await response.json();
        message.success('File uploaded successfully');
        setCurrentJob(job);
        fetchJobs();
        fetchRechargeHistory();
      } else {
        const error = await response.json();
        message.error(error.error || 'Upload failed');
      }
    } catch (error) {
      message.error('Upload failed');
    }
    setUploadLoading(false);
    setPendingFile(null);
    setExcelHeaders([]);
  };

  const handleProcessJob = async (jobId) => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      const response = await authFetch(getApiEndpoint(`/migration/recharge/jobs/${jobId}/process`), {
        method: 'POST',
      });

      if (response.ok) {
        message.success('Job processed successfully');
        fetchJobs();
      } else {
        message.error('Failed to process job');
      }
    } catch (error) {
      message.error('Failed to start processing');
    }
    setLoading(false);
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewDialog(true);
  };

  const handleEdit = (record) => {
    setEditingRecord({ ...record });
    form.setFieldsValue(record);
    setShowEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!editingRecord) return;

    const values = await form.validateFields();
    setLoading(true);
    try {
      const response = await authFetch(getApiEndpoint(`/migration/recharge/history/${editingRecord.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success('Record updated successfully');
        setShowEditDialog(false);
        setEditingRecord(null);
        form.resetFields();
        fetchRechargeHistory();
      } else {
        message.error('Failed to update record');
      }
    } catch (error) {
      message.error('Failed to update record');
    }
    setLoading(false);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this record?',
      onOk: async () => {
        try {
          const response = await authFetch(getApiEndpoint(`/migration/recharge/history/${id}`), {
            method: 'DELETE',
          });

          if (response.ok) {
            message.success('Record deleted successfully');
            fetchRechargeHistory();
          } else {
            message.error('Failed to delete record');
          }
        } catch (error) {
          message.error('Failed to delete record');
        }
      },
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      PENDING: { color: 'orange', text: 'Pending' },
      SUCCESS: { color: 'green', text: 'Success' },
      FAILED: { color: 'red', text: 'Failed' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getJobStatusTag = (status) => {
    const statusConfig = {
      CREATED: { color: 'blue', text: 'Created' },
      RUNNING: { color: 'orange', text: 'Running' },
      COMPLETED: { color: 'green', text: 'Completed' },
      FAILED: { color: 'red', text: 'Failed' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const historyColumns = [
    {
      title: 'No',
      key: 'no',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Source User',
      dataIndex: 'source_user',
      key: 'source_user',
    },
    {
      title: 'Platform User',
      dataIndex: 'user',
      key: 'user',
      render: (user) => user?.username || '-',
    },
    {
      title: 'Before Balance',
      dataIndex: 'before_balance',
      key: 'before_balance',
      render: (balance) => balance ? formatIndianCurrency(balance) : '-',
    },
    {
      title: 'After Balance',
      dataIndex: 'after_balance',
      key: 'after_balance',
      render: (balance) => balance ? formatIndianCurrency(balance) : '-',
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      render: (credit) => credit ? formatIndianCurrency(credit) : '-',
    },
    {
      title: 'GST Amount',
      dataIndex: 'gst_amount',
      key: 'gst_amount',
      render: (gst) => gst ? formatIndianCurrency(gst) : '-',
    },
    {
      title: 'Amount',
      dataIndex: 'recharge_amount',
      key: 'recharge_amount',
      render: (amount) => formatIndianCurrency(amount),
    },
    {
      title: 'Type',
      dataIndex: 'recharge_type',
      key: 'recharge_type',
    },
    {
      title: 'Sales No',
      dataIndex: 'sales_no',
      key: 'sales_no',
    },
    {
      title: 'Reference No',
      dataIndex: 'reference_no',
      key: 'reference_no',
    },
    {
      title: 'Payment Mode',
      dataIndex: 'payment_mode',
      key: 'payment_mode',
    },
    {
      title: 'Recharge Date',
      dataIndex: 'recharge_date',
      key: 'recharge_date',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Status',
      dataIndex: 'migration_status',
      key: 'migration_status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Failure Reason',
      dataIndex: 'failure_reason',
      key: 'failure_reason',
      render: (reason) => reason || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleView(record)}>
            <EyeOutlined />
          </Button>
          <Button size="small" onClick={() => handleEdit(record)}>
            <EditOutlined />
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  const jobColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Filename',
      dataIndex: 'source_filename',
      key: 'source_filename',
    },
    {
      title: 'Total Rows',
      dataIndex: 'total_rows',
      key: 'total_rows',
    },
    {
      title: 'Success',
      dataIndex: 'success_rows',
      key: 'success_rows',
    },
    {
      title: 'Failed',
      dataIndex: 'failed_rows',
      key: 'failed_rows',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getJobStatusTag(status),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold">Migrate Recharge History</h1>
        <Button onClick={() => { fetchRechargeHistory(); fetchJobs(); }} icon={<ReloadOutlined />}>
          Refresh
        </Button>
      </div>

      {/* Upload Section */}
      <Card title="Upload Recharge History" className="mb-6">
        <Upload
          beforeUpload={handleUpload}
          accept=".xlsx,.xls"
          showUploadList={false}
          disabled={uploadLoading}
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={uploadLoading}
            type="primary"
            size="large"
          >
            Bulk Upload Recharge History
          </Button>
        </Upload>
        <div className="mt-4 text-gray-500">
          <p>Supported format: Excel (.xlsx, .xls)</p>
          <p>After uploading, you will need to map your Excel columns to system fields.</p>
        </div>
      </Card>

      {/* Current Job Status */}
      {currentJob && (
        <Card title="Current Migration Job" className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <p><strong>File:</strong> {currentJob.source_filename}</p>
              <p><strong>Status:</strong> {getJobStatusTag(currentJob.status)}</p>
            </div>
            <div className="text-right">
              <p><strong>Total:</strong> {currentJob.total_rows}</p>
              <p><strong>Success:</strong> {currentJob.success_rows}</p>
              <p><strong>Failed:</strong> {currentJob.failed_rows}</p>
            </div>
          </div>
          {currentJob.total_rows > 0 && (
            <Progress
              percent={Math.round((currentJob.success_rows + currentJob.failed_rows) / currentJob.total_rows * 100)}
              status={currentJob.status === 'FAILED' ? 'exception' : 'active'}
              format={(percent) => `${currentJob.success_rows + currentJob.failed_rows} / ${currentJob.total_rows}`}
            />
          )}
          <div className="mt-4">
            {(currentJob.status === 'CREATED' || currentJob.status === 'FAILED') && (
              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                onClick={() => handleProcessJob(currentJob.id)}
                loading={loading}
              >
                Process Records
              </Button>
            )}
            {currentJob.status === 'RUNNING' && (
              <Button 
                icon={<PlayCircleOutlined />}
                disabled
              >
                Processing...
              </Button>
            )}
            {currentJob.status === 'COMPLETED' && (
              <Button 
                type="default"
                icon={<PlayCircleOutlined />}
                disabled
              >
                Completed
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card title="Recharge History" extra={
        <Space wrap>
          <Input
            placeholder="Search by user..."
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            style={{ width: 150 }}
          />
          <Select
            placeholder="Filter by status"
            value={searchStatus || undefined}
            onChange={setSearchStatus}
            style={{ width: 150 }}
            allowClear
          >
            <Select.Option value="PENDING">Pending</Select.Option>
            <Select.Option value="SUCCESS">Success</Select.Option>
            <Select.Option value="FAILED">Failed</Select.Option>
          </Select>
          <RangePicker
            onChange={setDateRange}
            placeholder={['Start date', 'End date']}
          />
          <Button onClick={fetchRechargeHistory}>Filter</Button>
        </Space>
      }
      >
        <Table
          columns={historyColumns}
          dataSource={Array.isArray(rechargeHistory) ? rechargeHistory : []}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* View Dialog */}
      <Modal
        title="Recharge Details"
        open={showViewDialog}
        onCancel={() => setShowViewDialog(false)}
        footer={[
          <Button key="close" onClick={() => setShowViewDialog(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedRecord && (
          <div>
            <p><strong>Source User:</strong> {selectedRecord.source_user}</p>
            <p><strong>Platform User:</strong> {selectedRecord.user?.username || '-'}</p>
            <p><strong>Amount:</strong> {formatIndianCurrency(selectedRecord.recharge_amount)}</p>
            <p><strong>Type:</strong> {selectedRecord.recharge_type}</p>
            <p><strong>Reference No:</strong> {selectedRecord.reference_no || '-'}</p>
            <p><strong>Payment Mode:</strong> {selectedRecord.payment_mode || '-'}</p>
            <p><strong>Remarks:</strong> {selectedRecord.remarks || '-'}</p>
            <p><strong>Recharge Date:</strong> {moment(selectedRecord.recharge_date).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Status:</strong> {getStatusTag(selectedRecord.migration_status)}</p>
            {selectedRecord.failure_reason && (
              <p><strong>Failure Reason:</strong> {selectedRecord.failure_reason}</p>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Dialog */}
      <Modal
        title="Edit Recharge Record"
        open={showEditDialog}
        onOk={handleUpdate}
        onCancel={() => {
          setShowEditDialog(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="remarks"
            label="Remarks"
            rules={[{ required: true, message: 'Please enter remarks' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter remarks" />
          </Form.Item>
        </Form>
      </Modal>

      <FieldMappingModal
        visible={showMappingModal}
        onCancel={() => {
          setShowMappingModal(false);
          setPendingFile(null);
          setExcelHeaders([]);
        }}
        onConfirm={handleMappingConfirm}
        headers={excelHeaders}
        loading={uploadLoading}
      />
    </div>
  );
};

export default MigrateRechargeHistoryPage;
